package com.evoapps.evoshift.domain.usecase.documents

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.DocumentFiles
import com.evoapps.evoshift.domain.model.DocumentProblem
import com.evoapps.evoshift.domain.model.InvalidDocumentException
import com.evoapps.evoshift.domain.model.PolicyReadReport
import com.evoapps.evoshift.domain.model.PolicyStatus
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffDocument
import com.evoapps.evoshift.domain.model.StaffDocumentType
import com.evoapps.evoshift.domain.model.StaffRecord
import com.evoapps.evoshift.domain.model.WorkplacePolicy
import com.evoapps.evoshift.domain.model.problems
import com.evoapps.evoshift.domain.repository.DocumentRepository
import com.evoapps.evoshift.domain.repository.StaffRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.staff.SaveStaffRecordUseCase
import kotlinx.datetime.LocalDate
import kotlin.time.Instant

/*
 * SH-122: the use cases behind people's documents and workplace policies. Who may call what is
 * the screens' (Gate) and the rules' (firestore.rules, storage.rules).
 */

/** [userId]'s documents at [workplaceId], newest first. */
class GetStaffDocumentsUseCase(private val documents: DocumentRepository) {
    suspend operator fun invoke(userId: String, workplaceId: String): Result<List<StaffDocument>> =
        documents.getStaffDocuments(userId, workplaceId).map { list -> list.sortedByDescending { it.uploadedAt } }
}

/** A file picked for upload: its name as the device knows it and its content. */
class PickedFile(val name: String?, val bytes: ByteArray)

/** What an upload did: the stored document, and the SH-47 date it recorded (null: none). */
data class StaffDocumentUpload(
    val document: StaffDocument,
    val updatedCheck: StaffCheck? = null
)

/**
 * Uploads a document about [StaffDocument.userId] at a workplace. With [updateStaffDates] (the
 * uploader records the SH-47 dates: owners and financial managers), a medical certificate's or an
 * OHS certificate's expiry date also becomes the recorded "valid until", through
 * [SaveStaffRecordUseCase], so the readiness statuses, the scheduling warnings and the dashboards'
 * reminders follow it. A person's own upload does not change the dates: the HR side checks it and
 * records them. Deleting a document leaves the dates as they are.
 */
class UploadStaffDocumentUseCase(
    private val documents: DocumentRepository,
    private val staff: StaffRepository,
    private val saveStaffRecord: SaveStaffRecordUseCase,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(
        userId: String,
        workplaceId: String,
        type: StaffDocumentType,
        file: PickedFile,
        expiresOn: LocalDate?,
        uploaderId: String,
        updateStaffDates: Boolean,
        onProgress: (Float) -> Unit = {}
    ): Result<StaffDocumentUpload> = runCatching {
        DocumentFiles.problemOf(file.bytes)?.let { throw InvalidDocumentException(it) }
        val contentType = DocumentFiles.sniff(file.bytes)!!
        val draft = StaffDocument(
            id = "",
            userId = userId,
            workplaceId = workplaceId,
            type = type,
            fileName = DocumentFiles.cleanFileName(file.name, contentType),
            contentType = contentType,
            sizeBytes = file.bytes.size.toLong(),
            expiresOn = expiresOn,
            uploadedAt = now(),
            uploadedBy = uploaderId
        )
        val stored = documents.uploadStaffDocument(draft, file.bytes, onProgress).getOrThrow()
        val check = type.check?.takeIf { updateStaffDates && expiresOn != null }
        // The document is in; a date that cannot be recorded is not a failed upload.
        val recorded = check?.let { recordDate(userId, workplaceId, it, expiresOn!!, uploaderId) } == true
        StaffDocumentUpload(stored, check.takeIf { recorded })
    }

    private suspend fun recordDate(userId: String, workplaceId: String, check: StaffCheck, validUntil: LocalDate, editorId: String): Boolean {
        val record = staff.getStaffRecord(userId, workplaceId).getOrNull() ?: StaffRecord(userId, workplaceId)
        val updated = when (check) {
            StaffCheck.MEDICAL_EXAM -> record.copy(medicalExamValidUntil = validUntil)
            StaffCheck.OHS_TRAINING -> record.copy(ohsValidUntil = validUntil)
        }
        if (updated.problems().isNotEmpty()) return false
        return saveStaffRecord(updated, editorId).isSuccess
    }
}

class DeleteStaffDocumentUseCase(private val documents: DocumentRepository) {
    suspend operator fun invoke(document: StaffDocument): Result<Unit> = documents.deleteStaffDocument(document)
}

class OpenStaffDocumentUseCase(private val documents: DocumentRepository) {
    suspend operator fun invoke(document: StaffDocument): Result<ByteArray> = documents.downloadStaffDocument(document)
}

/** [workplaceId]'s policies, newest first, each with [userId]'s own confirmation. */
class GetPoliciesUseCase(private val documents: DocumentRepository) {
    suspend operator fun invoke(workplaceId: String, userId: String): Result<List<PolicyStatus>> = runCatching {
        documents.getPolicies(workplaceId).getOrThrow()
            .sortedByDescending { it.publishedAt }
            .map { policy -> PolicyStatus(policy, documents.getAcknowledgement(workplaceId, policy.id, userId).getOrNull()) }
    }
}

/** Publishes a PDF under [title] for everyone at [workplaceId] to read. */
class PublishPolicyUseCase(
    private val documents: DocumentRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(workplaceId: String, title: String, file: PickedFile, publisherId: String, onProgress: (Float) -> Unit = {}): Result<WorkplacePolicy> = runCatching {
        val cleanTitle = title.trim().take(DocumentFiles.MAX_POLICY_TITLE)
        if (cleanTitle.isEmpty()) throw InvalidDocumentException(DocumentProblem.MISSING_TITLE)
        DocumentFiles.problemOf(file.bytes, setOf(DocumentFiles.PDF))?.let { throw InvalidDocumentException(it) }
        val policy = WorkplacePolicy(
            id = "",
            workplaceId = workplaceId,
            title = cleanTitle,
            fileName = DocumentFiles.cleanFileName(file.name, DocumentFiles.PDF),
            sizeBytes = file.bytes.size.toLong(),
            publishedAt = now(),
            publishedBy = publisherId
        )
        documents.publishPolicy(policy, file.bytes, onProgress).getOrThrow()
    }
}

class DeletePolicyUseCase(private val documents: DocumentRepository) {
    suspend operator fun invoke(policy: WorkplacePolicy): Result<Unit> = documents.deletePolicy(policy)
}

class OpenPolicyUseCase(private val documents: DocumentRepository) {
    suspend operator fun invoke(policy: WorkplacePolicy): Result<ByteArray> = documents.downloadPolicy(policy)
}

/** The signed-in person confirms reading a policy ("I have read it"; not a signature). */
class AcknowledgePolicyUseCase(private val documents: DocumentRepository) {
    suspend operator fun invoke(policy: WorkplacePolicy) = documents.acknowledge(policy.workplaceId, policy.id)
}

/** Who of [policy]'s workplace's current team confirmed it, and who has not yet (managers). */
class GetPolicyReadReportUseCase(
    private val documents: DocumentRepository,
    private val users: UserRepository
) {
    suspend operator fun invoke(policy: WorkplacePolicy): Result<PolicyReadReport> = runCatching {
        val acknowledged = documents.getAcknowledgements(policy.workplaceId, policy.id).getOrThrow().sortedBy { it.acknowledgedAt }
        val confirmed = acknowledged.mapTo(HashSet()) { it.userId }
        val team = users.getAssignableEmployees(policy.workplaceId).getOrThrow()
        val names = team.associate { it.uid to it.displayName }
        PolicyReadReport(
            policy = policy,
            acknowledged = acknowledged.map { ack -> ack.copy(displayName = names[ack.userId] ?: ack.displayName) },
            pending = team.filter { it.uid !in confirmed }.sortedBy { it.displayName.lowercase() }.map { it.uid to it.displayName },
            teamSize = team.size
        )
    }
}
